import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import wmAlert from '../helpers/wmAlerts'

export default defineComponent({
    computed: {
        ...mapGetters({
            hasPlan: 'user/hasPlan',
            isAdmin: 'user/isAdmin',
            isWorksheet: 'document/isWorksheet',
        }),
        ...mapState(['user', 'premium', 'document']),
        isOnEditor() {
            return window.isEditor || false
        },
        showSinglePrice() {
            if (!this.isOnEditor) return false

            if (!this.document.user_id) return true

            return !this.hasUserPurchasedThisDocument(this.document) && this.document.user_id == this.user.user.id
        },
    },
    methods: {
        async editPaywall(document, handler) {
            localStorage.setItem(
                'DocumentZoom',
                JSON.stringify({
                    id: this.document.id,
                    zoom: this.document.zoom,
                }),
            )
            const entity = document.entity_type
            const singlePurchaseEdits = window.localStorage.getItem('single_purchase_edits')
            const singlePurchases =
                document.single_purchase_edits > 0 && singlePurchaseEdits ? JSON.parse(singlePurchaseEdits) : []
            const documentHasBeenEdited = singlePurchases.includes(document.id)
            const documentHasPremiumFeatures = await this.$store.dispatch('document/getPremiumFeaturesByDocument', document)
            const canEdit = this.hasFeature('can-edit', document, document.entity_type, !!documentHasPremiumFeatures.length)

            if (
                this.isAdmin ||
                (canEdit && !this.hasUserPurchasedThisDocument(document)) ||
                (this.user.user.id === document.user_id && !documentHasPremiumFeatures) ||
                (document.entity_type === 'worksheet' && !documentHasPremiumFeatures.length)
            ) {
                const userResponse = await wmAlert.info({
                    html: `<div class='mx-2 text-center'>Are you sure you want to unpublish this ${entity}?</div>`,
                })

                if (userResponse.isConfirmed) {
                    handler(document)
                }

                return
            }

            if (this.hasUserPurchasedThisDocument(document)) {
                if (!documentHasBeenEdited) {
                    const userResponse = await wmAlert.confirm({
                        title: 'Unpublish & Edit?',
                        html: `<div class='text-center'>Are you sure you want to unpublish and change this ${entity}?
                                        <div class='text-danger font-weight-bold'>You can edit this document only once.</div>
                                    </div>`,
                    })

                    if (userResponse.isConfirmed && handler) {
                        singlePurchases.push(document.id)
                        localStorage.setItem('single_purchase_edits', JSON.stringify(singlePurchases))
                        handler(document)
                    }

                    return
                }
            }

            const userResponse = await wmAlert.confirm({
                title: 'This document is locked.',
                html: `
                            <p class='text-left' style='font-size: 20px'>This document includes premium features and cannot be edited with your current plan.</p>
                           `,
                showCloseButton: true,
                showDenyButton: true,
                confirmButtonText: 'Upgrade Plan',
                denyButtonText: 'Edit a Copy',
                denyButtonColor: '#8e8e8e',
                closButtonColor: '#000000',
                iconHtml:
                    '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="currentColor" class="bi bi-lock" viewBox="0 0 16 16"><path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2m3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2M5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1"/></svg>',
                customClass: {
                    confirmButton: 'swal2-confirm-edit',
                    denyButton: 'swal2-deny-edit',
                    actions: 'swal2-actions-edit',
                    icon: 'swal2-icon-edit',
                    closeButton: 'swal2-close-edit',
                },
            })

            if (userResponse.isDenied) {
                window.location.replace(`/worksheet/${document.id}/copy`)
            }

            if (userResponse.isConfirmed) {
                await this.goPremium()
            }
        },
        hasUserPurchasedThisDocument(document) {
            return (
                document?.user_has_purchased_document ||
                this.user.user.documents_purchased?.some((doc) => {
                    return doc.id === document.id
                })
            )
        },
        hasFeature(feature, document, docType, hasPremiumFeatures = null) {
            if (
                this.user.user.is_admin ||
                ['Gratis', 'Lifetime', 'Premium', 'Free Trial'].some((item) => item === this.user.user.subscription_plan)
            ) {
                return true
            }

            if (feature === 'full-access') {
                if (docType === 'crossword' || docType === 'wordsearch') return false
                return !(
                    (docType == 'worksheet' && this.user.user.subscription_plan !== 'Standard') ||
                    (docType == 'bingo' && !this.user.user.subscription_plan.includes('Bingo')) ||
                    (docType == 'flashcard' && !this.user.user.subscription_plan.includes('Flashcard'))
                )
            }

            if (feature === 'organize-folder') {
                return this.user.user.subscription_plan !== 'Free' && this.user.user.subscription_plan !== 'Basic'
            }

            if (feature === 'private') {
                return (
                    !this.isWorksheet ||
                    this.user.user.subscription_plan !== 'Free' ||
                    this.hasUserPurchasedThisDocument(document)
                )
            }

            if (['formatting', 'image', 'color', 'numbering'].includes(feature)) {
                return (
                    this.hasUserPurchasedThisDocument(document) ||
                    !(
                        (docType == 'worksheet' && this.user.user.subscription_plan !== 'Standard') ||
                        (docType == 'bingo' && !this.user.user.subscription_plan.includes('Bingo')) ||
                        (docType == 'flashcard' && !this.user.user.subscription_plan.includes('Flashcard'))
                    )
                )
            }

            if (feature === 'remove-logo') {
                return (
                    this.hasUserPurchasedThisDocument(document) ||
                    (this.user.user.subscription_plan !== 'Free' && this.user.user.subscription_plan !== 'Basic')
                )
            }

            if (feature === 'unpublish-and-edit') {
                let hasEdited = document.single_purchase_edits > 0
                let singlePurchases = []
                const singlePurchaseEdits = window.localStorage.getItem('single_purchase_edits')
                if (hasEdited && singlePurchaseEdits) {
                    try {
                        singlePurchases = JSON.parse(singlePurchaseEdits)
                        hasEdited = singlePurchases.includes(document.id)
                    } catch (e) {}
                }
                return !(this.hasUserPurchasedThisDocument(document) && hasEdited)
            }

            if (feature === 'can-edit') {
                return (
                    (docType === 'worksheet' && this.user.user.subscription_plan === 'Basic' && !hasPremiumFeatures) ||
                    (docType === 'worksheet' && 'Standard' === this.user.user.subscription_plan) ||
                    (docType === 'bingo' && this.user.user.subscription_plan.includes('Bingo')) ||
                    (docType === 'flashcard' && this.user.user.subscription_plan.includes('Flashcard'))
                )
            }
        },
    },
})
